import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const DISMISS_KEY = 'push_banner_dismissed_until';
const SNOOZE_HOURS = 24;
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;
const isStandalonePwa = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const PushPermissionBanner = () => {
  const { supported, permission, isSubscribed, subscribe, isBusy, canPromptDriver } = usePushNotifications();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      setDismissed(Date.now() < until);
    } catch (e) { setDismissed(false); }
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + SNOOZE_HOURS * 3600 * 1000)); } catch (e) {}
    setDismissed(true);
  };

  if (!canPromptDriver || dismissed || isSubscribed || permission === 'granted') return null;

  if (isIos() && !isStandalonePwa()) {
    return (
      <div className="mb-4 p-4 rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-700/10" data-testid="push-ios-install-banner">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-white text-sm">Active les notifications</p>
            <p className="text-amber-200/90 text-xs mt-1 leading-relaxed">Pour recevoir les notifications même app fermée, installe ProPilote sur ton écran d&apos;accueil&nbsp;:</p>
            <ol className="text-amber-100/90 text-xs mt-2 ml-1 space-y-0.5 list-decimal list-inside">
              <li>Touche le bouton <strong>Partager</strong> (carré + flèche ↑)</li>
              <li>Choisis <strong>« Sur l&apos;écran d&apos;accueil »</strong></li>
              <li>Ouvre ProPilote depuis l&apos;icône, puis reviens ici</li>
            </ol>
          </div>
          <button onClick={handleDismiss} className="text-amber-300/70 hover:text-white p-1" data-testid="push-banner-dismiss" aria-label="Reporter"><X className="w-4 h-4" /></button>
        </div>
      </div>
    );
  }

  if (!supported) return null;
  return (
    <div className="mb-4 p-4 rounded-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-700/10" data-testid="push-permission-banner">
      <div className="flex items-start gap-3">
        <Bell className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm">Active les notifications</p>
          <p className="text-emerald-100/80 text-xs mt-1 leading-relaxed">Reçois une alerte instantanée pour chaque nouvelle commande, modification ou message — même quand l&apos;app est fermée.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={subscribe} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-xs font-semibold transition-colors" data-testid="push-banner-enable">
              <Bell className="w-3.5 h-3.5" />{isBusy ? 'Activation…' : 'Activer'}
            </button>
            <button onClick={handleDismiss} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors" data-testid="push-banner-snooze">
              <BellOff className="w-3.5 h-3.5" />Plus tard
            </button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-slate-400 hover:text-white p-1" aria-label="Fermer" data-testid="push-banner-close"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};
export default PushPermissionBanner;
